export default class ContinuousAudioPlayer {
  constructor(options = {}) {
    this.sampleRate = options.sampleRate || 22050;
    this.channels = options.channels || 1;
    this.volume = options.volume || 1;
    this.playbackRate = options.playbackRate || 0.85;
    this.fadeDuration = options.fadeDuration || 50;
    this.filterFrequency = options.filterFrequency || 8000;
    this.smallChunkThreshold = options.smallChunkThreshold || 5000;

    this.audioContext = null;
    this.gainNode = null;
    this.filterNode = null;
    this.scriptNode = null;

    this.isPlaying = false;
    this.isPaused = false;
    this.isCompleted = false;

    this.onStatusUpdate = options.onStatusUpdate || (() => {});
    this.onPlaybackComplete = options.onPlaybackComplete || (() => {});

    this.continuousBuffer = new Float32Array(0);
    this.fullAudioBuffer = new Float32Array(0); // 保存完整音频数据用于重播
    this.playbackPosition = 0;

    this.smallChunksBuffer = [];
    this.smallChunksSize = 0;
    this.lastChunkTime = 0;
    this.lastDataTime = Date.now();

    this.previousFrame = new Float32Array(128);
    this.hasPreviousFrame = false;
  }

  initAudioContext() {
    if (this.audioContext) return this.audioContext;

    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new AudioContext({
        latencyHint: "interactive",
        sampleRate: 22050,
      });

      this.gainNode = this.audioContext.createGain();
      this.gainNode.gain.value = this.volume;

      this.filterNode = this.audioContext.createBiquadFilter();
      this.filterNode.type = "lowpass";
      this.filterNode.frequency.value = this.filterFrequency;
      this.filterNode.Q.value = 0.7;

      this.scriptNode = this.audioContext.createScriptProcessor(1024, 1, 1);
      this.scriptNode.onaudioprocess = this.processAudio.bind(this);

      this.scriptNode.connect(this.filterNode);
      this.filterNode.connect(this.gainNode);
      this.gainNode.connect(this.audioContext.destination);

      return this.audioContext;
    } catch (err) {
      // throw err;
      console.log(err);
    }
  }

  start() {
    if (this.isPlaying && !this.isPaused) return false;

    try {
      this.initAudioContext();

      if (this.audioContext.state === "suspended") {
        this.audioContext.resume();
      }

      this.isPlaying = true;
      this.isPaused = false;

      if (!this.scriptNode) {
        this.scriptNode = this.audioContext.createScriptProcessor(1024, 1, 1);
        this.scriptNode.onaudioprocess = this.processAudio.bind(this);
        this.scriptNode.connect(this.filterNode);
      }

      this.onStatusUpdate("正在播放");

      return true;
    } catch (err) {
      return false;
    }
  }

  pause() {
    if (!this.isPlaying || this.isPaused) return false;

    try {
      this.audioContext.suspend();
      this.isPaused = true;
      this.onStatusUpdate("已暂停");

      return true;
    } catch (err) {
      return false;
    }
  }

  resume() {
    if (!this.isPlaying || !this.isPaused) return false;

    try {
      this.audioContext.resume();
      this.isPaused = false;
      this.onStatusUpdate("正在播放");

      return true;
    } catch (err) {
      return false;
    }
  }

  stop() {
    if (!this.isPlaying) return false;

    try {
      // 暂停 AudioContext 以确保完全停止音频处理
      if (this.audioContext && this.audioContext.state === "running") {
        this.audioContext.suspend();
      }

      if (this.scriptNode) {
        this.scriptNode.disconnect();
        this.scriptNode = null;
      }

      this.isPlaying = false;
      this.isPaused = false;
      this.isCompleted = false;
      this.playbackPosition = 0;

      this.continuousBuffer = new Float32Array(0);
      this.fullAudioBuffer = new Float32Array(0); // 同时清空完整音频缓冲区
      this.smallChunksBuffer = [];
      this.smallChunksSize = 0;
      this.hasPreviousFrame = false;
      this.lastDataTime = Date.now();

      this.onStatusUpdate("已停止");

      return true;
    } catch (err) {
      return false;
    }
  }
  mute() {
    if (this.gainNode) {
      this.gainNode.gain.value = 0;
    }
  }

  unmute() {
    if (this.gainNode) {
      this.gainNode.gain.value = this.volume;
    }
  }
  // 重置播放位置但保留音频数据，用于重播
  reset() {
    try {
      if (this.scriptNode) {
        this.scriptNode.disconnect();
        this.scriptNode = null;
      }

      this.isPlaying = false;
      this.isPaused = false;
      this.isCompleted = false;
      this.playbackPosition = 0;

      // 从完整音频缓冲区恢复数据，确保从头开始播放
      if (this.fullAudioBuffer.length > 0) {
        this.continuousBuffer = new Float32Array(this.fullAudioBuffer);
      }

      this.smallChunksBuffer = [];
      this.smallChunksSize = 0;
      this.hasPreviousFrame = false;

      this.onStatusUpdate("已重置");

      return true;
    } catch (err) {
      return false;
    }
  }

  addPCMData(pcmData, isPaused = false) {
    if (!pcmData || pcmData.length === 0) return;
    if (isPaused) {
      this.stop();
      console.log(444, this.isPaused);
      return; // 如果是暂停状态，直接返回，不处理新数据
    }

    // 如果已经停止播放，不接受新的音频数据
    if (!this.isPlaying && this.isCompleted) {
      return;
    }

    try {
      const now = Date.now();
      this.lastDataTime = now; // 更新最后接收数据的时间

      if (pcmData.length < this.smallChunkThreshold) {
        this.smallChunksBuffer.push(pcmData);
        this.smallChunksSize += pcmData.length;
        this.lastChunkTime = now;

        if (
          this.smallChunksSize >= this.smallChunkThreshold ||
          (this.smallChunksBuffer.length > 0 && now - this.lastChunkTime > 100)
        ) {
          this.processSmallChunks();
        }
      } else {
        if (this.smallChunksBuffer.length > 0) {
          this.processSmallChunks();
        }

        this.appendToContinuousBuffer(pcmData);
      }

      if (
        this.isPlaying &&
        !this.isPaused &&
        this.audioContext &&
        this.audioContext.state !== "running"
      ) {
        this.audioContext.resume();
      }
    } catch (err) {
      console.error(`添加PCM数据失败: ${err.message}`);
    }
  }

  processSmallChunks() {
    if (this.smallChunksBuffer.length === 0) return;

    try {
      const totalLength = this.smallChunksSize;
      const mergedData = new Float32Array(totalLength);

      let offset = 0;
      for (const chunk of this.smallChunksBuffer) {
        mergedData.set(chunk, offset);
        offset += chunk.length;
      }

      this.appendToContinuousBuffer(mergedData);

      this.smallChunksBuffer = [];
      this.smallChunksSize = 0;
    } catch (err) {
      console.error(`处理小音频片段失败: ${err.message}`);
    }
  }

  appendToContinuousBuffer(pcmData) {
    // 如果已经停止，不添加新数据
    if (!this.isPlaying && this.isCompleted) {
      return;
    }

    // 更新播放缓冲区
    const newBuffer = new Float32Array(
      this.continuousBuffer.length + pcmData.length
    );
    newBuffer.set(this.continuousBuffer);
    newBuffer.set(pcmData, this.continuousBuffer.length);
    this.continuousBuffer = newBuffer;

    // 同时更新完整音频缓冲区用于重播
    const newFullBuffer = new Float32Array(
      this.fullAudioBuffer.length + pcmData.length
    );
    newFullBuffer.set(this.fullAudioBuffer);
    newFullBuffer.set(pcmData, this.fullAudioBuffer.length);
    this.fullAudioBuffer = newFullBuffer;

    // 只有在明确需要播放且未停止的情况下才自动开始
    if (!this.isPlaying && !this.isCompleted) {
      this.start();
    }
  }

  processAudio(audioProcessingEvent) {
    const outputBuffer = audioProcessingEvent.outputBuffer;
    const outputData = outputBuffer.getChannelData(0);
    const bufferSize = outputData.length;

    // 检查是否播放完成
    const now = Date.now();
    const noNewDataTimeout = 3000; // 3秒没有新数据认为播放完成
    const hasReceivedData = this.continuousBuffer.length > 0; // 确保已经接收到音频数据
    const isPlaybackComplete =
      hasReceivedData &&
      this.playbackPosition >= this.continuousBuffer.length &&
      now - this.lastDataTime > noNewDataTimeout;

    if (isPlaybackComplete && !this.isCompleted) {
      this.isCompleted = true;
      this.onStatusUpdate("播放完成");
      this.onPlaybackComplete();
      // 停止播放
      for (let i = 0; i < bufferSize; i++) {
        outputData[i] = 0;
      }
      return;
    }

    if (
      this.playbackPosition >= this.continuousBuffer.length ||
      this.isPaused
    ) {
      for (let i = 0; i < bufferSize; i++) {
        outputData[i] = 0;
      }
      return;
    }

    const resampleRatio =
      this.sampleRate / this.audioContext.sampleRate / this.playbackRate;

    for (let i = 0; i < bufferSize; i++) {
      const srcIndex = this.playbackPosition + i * resampleRatio;
      const srcIndexInt = Math.floor(srcIndex);
      const fraction = srcIndex - srcIndexInt;

      if (srcIndexInt >= this.continuousBuffer.length - 1) {
        outputData[i] = 0;
        continue;
      }

      let sample =
        this.continuousBuffer[srcIndexInt] * (1 - fraction) +
        this.continuousBuffer[srcIndexInt + 1] * fraction;

      if (this.hasPreviousFrame) {
        const frameIndex = i % this.previousFrame.length;
        const smoothFactor = 0.1;
        sample =
          sample * (1 - smoothFactor) +
          this.previousFrame[frameIndex] * smoothFactor;
        this.previousFrame[frameIndex] = sample;
      } else {
        this.previousFrame[i % this.previousFrame.length] = sample;
        if (i === bufferSize - 1) {
          this.hasPreviousFrame = true;
        }
      }

      const fadeSamples = Math.floor(
        (this.fadeDuration * this.audioContext.sampleRate) / 1000
      );

      if (this.playbackPosition < fadeSamples) {
        const fadeInFactor = this.playbackPosition / fadeSamples;
        sample *= fadeInFactor;
      }

      const remainingSamples = this.continuousBuffer.length - srcIndexInt;
      if (remainingSamples < fadeSamples) {
        const fadeOutFactor = remainingSamples / fadeSamples;
        sample *= fadeOutFactor;
      }

      outputData[i] = sample * 0.95;
    }

    this.playbackPosition += bufferSize * resampleRatio;

    const maxBufferLength = this.audioContext.sampleRate * 10;
    if (this.playbackPosition > maxBufferLength) {
      const newBuffer = new Float32Array(
        this.continuousBuffer.length - Math.floor(maxBufferLength / 2)
      );
      newBuffer.set(
        this.continuousBuffer.subarray(Math.floor(maxBufferLength / 2))
      );
      this.continuousBuffer = newBuffer;
      this.playbackPosition -= Math.floor(maxBufferLength / 2);
    }

    this.onStatusUpdate(
      `正在播放 (位置: ${Math.floor(this.playbackPosition)}/${
        this.continuousBuffer.length
      })`
    );
  }
}
// export {ContinuousAudioPlayer}
